import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Badge,
  Box,
  ButtonBase,
  CircularProgress,
  Fab,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDeviceLayout } from '../../../hooks/useDeviceLayout';
import { useMobileEdgeTabDragOffset } from '../../../hooks/useMobileEdgeTabDragOffset';
import {
  MOBILE_EDGE_TAB_HEIGHT,
  MOBILE_EDGE_TAB_WIDTH,
  mobileEdgeTabButtonSx,
  mobileEdgeTabFixedSx,
  mobileEdgeTabLabelSx,
  mobileEdgeTabBottom,
} from '../../../components/layout/floatingDockMobileEdge';
import { DOCK_CSS } from '../../../components/layout/floatingDockLayout';
import type { TeamChatConversation } from '../../../services/teamChatService';
import TeamChatAvatar from '../components/TeamChatAvatar';
import TeamChatNewConversationDialog from '../components/TeamChatNewConversationDialog';
import {
  TEAM_CHAT_DOCK_BAR_HEIGHT,
  getTeamChatDockZ,
  TEAM_CHAT_THEME,
} from '../teamChatTheme';
import { formatConversationTimestamp } from '../teamChatUtils';
import TeamChatFloatingWindow from './TeamChatFloatingWindow';
import TeamChatMinimizedBar from './TeamChatMinimizedBar';
import { useTeamChatDockController } from './useTeamChatDockController';

interface TeamChatDockProps {
  unreadTotal?: number;
  onUnreadChange?: (total: number) => void;
  visuallyHidden?: boolean;
  launcherBottomOffset?: number;
}

const TeamChatDock: React.FC<TeamChatDockProps> = ({
  unreadTotal = 0,
  onUnreadChange,
  visuallyHidden = false,
  launcherBottomOffset = 16,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobileMd: isMobile } = useDeviceLayout();
  const teamEdgeDrag = useMobileEdgeTabDragOffset('team');
  const theme = useTheme();
  const dockZ = getTeamChatDockZ(theme);
  const threeColumns = useMediaQuery('(min-width:1280px)');
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const controller = useTeamChatDockController({
    maxExpanded: isMobile ? 1 : threeColumns ? 3 : 2,
    onUnreadChange,
    onError: setError,
    browserNotificationsEnabled: !location.pathname.startsWith('/team-chat'),
    dockVisible: !visuallyHidden,
  });

  const calculatedUnread = controller.conversations.reduce(
    (sum, conversation) =>
      controller.openIds.includes(conversation.id)
        ? sum
        : sum + (conversation.unread_count || 0),
    0,
  );
  const badgeTotal = Math.max(unreadTotal, calculatedUnread);

  const fabBottomCss =
    launcherBottomOffset === 16 ? DOCK_CSS.rowBottom : `${launcherBottomOffset}px`;
  const fabBottom = launcherBottomOffset;
  const windowBottom = fabBottom + TEAM_CHAT_DOCK_BAR_HEIGHT + 8;

  const filteredConversations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return controller.conversations;
    return controller.conversations.filter((conversation) =>
      [
        conversation.title,
        conversation.last_message_preview,
        ...conversation.participants.map((participant) => participant.full_name),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [controller.conversations, search]);

  const expandedConversations = controller.expandedIds
    .map((id) => controller.conversations.find((conversation) => conversation.id === id))
    .filter(Boolean) as TeamChatConversation[];
  const dockBarConversations = controller.openIds
    .map((id) => controller.conversations.find((conversation) => conversation.id === id))
    .filter(Boolean) as TeamChatConversation[];

  const openFromList = async (conversation: TeamChatConversation) => {
    setLauncherOpen(false);
    await controller.openConversation(conversation);
  };

  useEffect(() => {
    if (!launcherOpen && controller.expandedIds.length === 0) return;

    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-team-chat-surface="true"]')) return;

      // Menus, popovers e diálogos abertos a partir do chat usam portal.
      if (
        target.closest(
          '.MuiPopover-root, .MuiMenu-root, .MuiDialog-root, .MuiModal-root, #quick-messages-popover',
        )
      ) {
        return;
      }

      setLauncherOpen(false);
      controller.minimizeAllExpanded();
    };

    document.addEventListener('pointerdown', handleOutsidePointerDown);
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown);
  }, [controller.expandedIds, controller.minimizeAllExpanded, launcherOpen]);

  const handleDockBarSelect = (conversationId: string) => {
    controller.restoreConversation(conversationId);
  };

  // Mantém o controlador/realtime montado nas páginas onde a UI flutuante não deve competir com composers.
  if (visuallyHidden) return null;

  const activeMobileConversation = isMobile
    ? expandedConversations.find((conversation) => conversation.id === controller.focusedId) ||
      expandedConversations[expandedConversations.length - 1]
    : null;

  const dockUi = (
    <>
      {!activeMobileConversation && (
        <Tooltip
          title={
            isMobile
              ? launcherOpen
                ? 'Fechar chat da equipe (arraste verticalmente para reposicionar)'
                : 'Abrir chat da equipe (arraste verticalmente para reposicionar)'
              : launcherOpen
                ? 'Fechar chat da equipe'
                : 'Abrir chat da equipe'
          }
          placement="left"
        >
          <Badge
            color="error"
            badgeContent={badgeTotal}
            max={99}
            overlap="circular"
            invisible={!isMobile || !badgeTotal}
            anchorOrigin={isMobile ? { vertical: 'top', horizontal: 'left' } : undefined}
            sx={{
              position: 'fixed',
              zIndex: dockZ.launcherFab,
              ...(isMobile
                ? { ...mobileEdgeTabFixedSx, bottom: teamEdgeDrag.bottomCss, touchAction: 'none' }
                : {
                    right: `calc(env(safe-area-inset-right, 0px) + ${DOCK_CSS.teamFabRight})`,
                    bottom: `calc(env(safe-area-inset-bottom, 0px) + ${fabBottomCss})`,
                  }),
            }}
          >
            {isMobile ? (
              <ButtonBase
                data-team-chat-surface="true"
                aria-label={
                  launcherOpen
                    ? 'Fechar chat da equipe. Arraste para cima ou para baixo para ajustar a posição.'
                    : 'Abrir chat da equipe. Arraste para cima ou para baixo para ajustar a posição.'
                }
                {...teamEdgeDrag.bindDragSurface()}
                onClick={teamEdgeDrag.consumeClickIfDragged(() => setLauncherOpen((current) => !current))}
                sx={{
                  ...mobileEdgeTabButtonSx,
                  cursor: 'grab',
                  background: `linear-gradient(165deg, ${TEAM_CHAT_THEME.primary} 0%, #1D4ED8 100%)`,
                  '&:hover': {
                    filter: 'brightness(1.06)',
                    transform: 'translateX(-2px)',
                    background: `linear-gradient(165deg, #2563EB 0%, #1E40AF 100%)`,
                  },
                  '&:active': { cursor: 'grabbing' },
                }}
              >
                {launcherOpen ? (
                  <>
                    <CloseIcon sx={{ fontSize: 17 }} aria-hidden />
                    <Box component="span" sx={mobileEdgeTabLabelSx}>Fechar</Box>
                  </>
                ) : (
                  <>
                    <ForumOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
                    <Box component="span" sx={mobileEdgeTabLabelSx}>Equipe</Box>
                  </>
                )}
              </ButtonBase>
            ) : (
              <Fab
                data-team-chat-surface="true"
                aria-label={launcherOpen ? 'Fechar chat da equipe' : 'Abrir chat da equipe'}
                onClick={() => setLauncherOpen((current) => !current)}
                sx={{
                  width: DOCK_CSS.fabSize,
                  height: DOCK_CSS.fabSize,
                  minHeight: DOCK_CSS.fabSize,
                  color: '#fff',
                  bgcolor: TEAM_CHAT_THEME.primary,
                  boxShadow: '0 10px 30px rgba(37,99,235,.36)',
                  '&:hover': { bgcolor: '#1D4ED8', transform: 'translateY(-2px)' },
                  transition: 'transform .2s ease, box-shadow .2s ease',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}
              >
                <Badge color="error" badgeContent={badgeTotal} max={99} overlap="circular">
                  {launcherOpen ? <CloseIcon /> : <ForumOutlinedIcon />}
                </Badge>
              </Fab>
            )}
          </Badge>
        </Tooltip>
      )}

      {launcherOpen && !activeMobileConversation && (
        <Paper
          data-team-chat-surface="true"
          elevation={0}
          sx={{
            position: 'fixed',
            zIndex: dockZ.launcherPanel,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#fff',
            border: `1px solid ${TEAM_CHAT_THEME.border}`,
            boxShadow: '0 18px 48px rgba(15,23,42,.22)',
            ...(isMobile
              ? {
                  inset: 0,
                  width: '100vw',
                  height: '100dvh',
                  borderRadius: 0,
                }
              : {
                  right: `calc(env(safe-area-inset-right, 0px) + ${DOCK_CSS.teamFabRight})`,
                  bottom: `calc(env(safe-area-inset-bottom, 0px) + ${fabBottomCss} + ${DOCK_CSS.fabSize} + 10px)`,
                  width: 360,
                  height: 'min(530px, calc(100dvh - 110px))',
                  borderRadius: 3,
                }),
          }}
        >
          <Box
            sx={{
              px: 2,
              pt: isMobile ? 'max(16px, env(safe-area-inset-top, 0px))' : 1.75,
              pb: 1.5,
              borderBottom: `1px solid ${TEAM_CHAT_THEME.borderSoft}`,
              backgroundImage: `linear-gradient(${TEAM_CHAT_THEME.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${TEAM_CHAT_THEME.gridLine} 1px, transparent 1px)`,
              backgroundSize: '24px 24px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: TEAM_CHAT_THEME.navy }}>
                  Chat da equipe
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: TEAM_CHAT_THEME.slate }}>
                  {controller.connected ? 'Conectado' : 'Reconectando…'}
                  {badgeTotal ? ` · ${badgeTotal} não lida${badgeTotal === 1 ? '' : 's'}` : ''}
                </Typography>
              </Box>
              <Tooltip title="Nova conversa ou grupo">
                <IconButton
                  onClick={() => setNewConversationOpen(true)}
                  sx={{ color: '#fff', bgcolor: TEAM_CHAT_THEME.tealDark, '&:hover': { bgcolor: '#0F766E' } }}
                >
                  <AddCommentOutlinedIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Abrir página completa">
                <IconButton onClick={() => navigate('/team-chat')}>
                  <LaunchIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </Tooltip>
              {isMobile && (
                <IconButton onClick={() => setLauncherOpen(false)}>
                  <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar colega ou grupo"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{
                mt: 1.25,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  bgcolor: '#fff',
                  fontSize: '0.82rem',
                  '& fieldset': { borderColor: TEAM_CHAT_THEME.borderSoft },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: TEAM_CHAT_THEME.slateFaint }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              py: 0.75,
              '&::-webkit-scrollbar': { width: 5 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(15,23,42,.18)', borderRadius: 99 },
            }}
          >
            {controller.loadingConversations ? (
              <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}>
                <CircularProgress size={26} />
              </Box>
            ) : filteredConversations.length === 0 ? (
              <Box sx={{ px: 3, py: 7, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.82rem', color: TEAM_CHAT_THEME.slate }}>
                  {search ? 'Nenhuma conversa encontrada.' : 'Inicie uma conversa com alguém da equipe.'}
                </Typography>
              </Box>
            ) : (
              filteredConversations.map((conversation) => {
                const counterpart = conversation.counterpart;
                const online = counterpart
                  ? controller.onlineUserIds.has(counterpart.user_id)
                  : undefined;
                return (
                  <Box
                    key={conversation.id}
                    onClick={() => void openFromList(conversation)}
                    sx={{
                      mx: 0.75,
                      px: 1.25,
                      py: 1,
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: TEAM_CHAT_THEME.mutedAlt },
                    }}
                  >
                    <Badge
                      color="error"
                      badgeContent={conversation.unread_count}
                      max={99}
                      overlap="circular"
                    >
                      <TeamChatAvatar
                        name={conversation.title}
                        avatarUrl={conversation.avatar_url}
                        gender={counterpart?.gender}
                        isGroup={conversation.type === 'group'}
                        online={conversation.type === 'direct' ? Boolean(online) : undefined}
                        size={42}
                      />
                    </Badge>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          noWrap
                          sx={{
                            flex: 1,
                            fontSize: '0.84rem',
                            fontWeight: conversation.unread_count ? 700 : 600,
                            color: TEAM_CHAT_THEME.navy,
                          }}
                        >
                          {conversation.title}
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: TEAM_CHAT_THEME.slateFaint }}>
                          {formatConversationTimestamp(conversation.last_message_at)}
                        </Typography>
                      </Box>
                      <Typography
                        noWrap
                        sx={{
                          mt: 0.2,
                          fontSize: '0.73rem',
                          color: TEAM_CHAT_THEME.slate,
                          fontWeight: conversation.unread_count ? 600 : 400,
                        }}
                      >
                        {conversation.last_message_preview || 'Conversa iniciada'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Paper>
      )}

      {!isMobile &&
        expandedConversations.map((conversation, index) => (
          <TeamChatFloatingWindow
            key={conversation.id}
            conversation={conversation}
            controller={controller}
            position={index}
            bottomOffset={windowBottom}
          />
        ))}

      {activeMobileConversation && (
        <TeamChatFloatingWindow
          conversation={activeMobileConversation}
          controller={controller}
          position={0}
          mobile
        />
      )}

      {!isMobile && (
        <TeamChatMinimizedBar
          conversations={dockBarConversations}
          expandedIds={controller.expandedIds}
          focusedId={controller.focusedId}
          bottomOffset={fabBottom}
          onSelect={handleDockBarSelect}
          onClose={controller.closeConversation}
        />
      )}

      <TeamChatNewConversationDialog
        open={newConversationOpen}
        onlineUserIds={controller.onlineUserIds}
        onClose={() => setNewConversationOpen(false)}
        onStartDirect={controller.startDirect}
        onCreateGroup={controller.createGroup}
      />

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={4500}
        message={error}
        onClose={() => setError('')}
        sx={{ zIndex: dockZ.overlay + 2 }}
      />
    </>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(dockUi, document.body);
};

export default TeamChatDock;
